import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';

import { DashboardTemplate } from 'modules/v2/templates';
import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getModOrders } from 'modules/api/digitalServices/mailOnDemand';
import {
  getRouteMailOnDemandNew,
  getRouteMailOnDemandOrder,
  getRouteMailOnDemandResume,
} from 'modules/v2/routes/navigation';

import StatusChip from '../components/StatusChip';
import { CoverLetterChip } from '../components/CoverLetterChip';
import CoverLetterMiniPreview from '../components/CoverLetterMiniPreview';
import strings, { formatMessage } from '../strings';
import { RESUMABLE_STATUSES } from '../constants';
import { excludeBookPrintOrders, formatCredits, formatDate } from '../utils';

const STATUS_TAB_ORDER = [
  'review',
  'paid',
  'accepted',
  'in_production',
  'shipped',
  'cancelled',
  'draft',
  'resolving',
  'paying',
  'resolution_failed',
];

export const MailOnDemandOrdersList = () => {
  const history = useHistory();
  // Book print tickets (origin 'book') are not mailings; DS stops sending them,
  // and this keeps them out of the list if one still arrives.
  const { data: orders, isLoading, isError, refetch } = useQuery('modOrders', getModOrders, {
    select: excludeBookPrintOrders,
  });
  const [activeStatus, setActiveStatus] = useState('all');
  const [search, setSearch] = useState('');

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.orders.loading}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 py-6">
        <p>{strings.orders.loadError}</p>
        <Button color="outlined" onClick={() => refetch()}>
          {strings.common.retry}
        </Button>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="mod-orders-empty">
        <h3 className="mb-2 text-lg font-semibold">{strings.orders.emptyTitle}</h3>
        <p className="mb-6 text-neutral-500">{strings.orders.emptyBody}</p>
        <Button onClick={() => history.push(getRouteMailOnDemandNew())}>
          {strings.orders.newOrder}
        </Button>
      </div>
    );
  }

  const present = STATUS_TAB_ORDER.filter((s) => orders.some((o) => o.status === s));
  const countFor = (s) => orders.filter((o) => o.status === s).length;
  const q = search.trim().toLowerCase();
  const filtered = orders.filter(
    (o) =>
      (activeStatus === 'all' || o.status === activeStatus) &&
      (q === '' || (o.asset?.title || '').toLowerCase().includes(q)),
  );

  const tabClass = (isActive) =>
    `rounded-full px-3 py-1 text-sm ${
      isActive ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700'
    }`;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <input
          type="text"
          data-testid="mod-orders-search"
          aria-label={strings.orders.searchPlaceholder}
          placeholder={strings.orders.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-neutral-200 px-3 py-2 text-sm sm:w-64"
        />
        <Button onClick={() => history.push(getRouteMailOnDemandNew())} data-testid="mod-new-order">
          {strings.orders.newOrder}
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="mod-orders-tab-all"
          aria-pressed={activeStatus === 'all'}
          onClick={() => setActiveStatus('all')}
          className={tabClass(activeStatus === 'all')}
        >
          {strings.orders.filterAll}
          <span className="ml-1.5 opacity-70">{orders.length}</span>
        </button>
        {present.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`mod-orders-tab-${s}`}
            aria-pressed={activeStatus === s}
            onClick={() => setActiveStatus(s)}
            className={tabClass(activeStatus === s)}
          >
            {strings.statuses[s]}
            <span className="ml-1.5 opacity-70">{countFor(s)}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="py-8 text-center text-neutral-500" data-testid="mod-orders-no-match">
          {strings.orders.noMatch}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {filtered.map((order) => {
            const isResumable = RESUMABLE_STATUSES.includes(order.status);

            return (
              <li
                key={order._id}
                data-testid={`mod-order-row-${order._id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-none items-center gap-2">
                    {order?.asset?.thumbnailUrl && (
                      <img
                        src={order.asset.thumbnailUrl}
                        alt=""
                        className="h-14 w-11 flex-none rounded border border-neutral-200 object-cover"
                      />
                    )}
                    {order.coverLetter && (
                      <div className="flex-none overflow-hidden rounded border border-neutral-200">
                        <CoverLetterMiniPreview
                          templateKey={order.coverLetter.templateKey}
                          body={order.coverLetter.editedBody}
                          width={40}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate font-medium">{order.asset?.title}</span>
                    <span className="text-xs text-neutral-500">
                      {formatMessage(strings.orders.createdAt, { date: formatDate(order.createdAt) })}
                      {(() => {
                        const n =
                        order.resolution?.selectedMailableCount ??
                        order.resolution?.mailableCount;

                        if (n === undefined) {
                          return null;
                        }

                        return (
                          <>
                            {' · '}
                            {formatMessage(
                              n === 1
                                ? strings.orders.recipientCountSingular
                                : strings.orders.recipientsCount,
                              { count: formatCredits(n) },
                            )}
                          </>
                        );
                      })()}
                    </span>
                    {order.coverLetter && (
                      <span className="mt-0.5">
                        <CoverLetterChip templateKey={order.coverLetter.templateKey} />
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusChip status={order.status} />
                  {isResumable ? (
                    <Button
                      size="sm"
                      onClick={() => history.push(getRouteMailOnDemandResume(order._id))}
                      data-testid={`mod-continue-order-${order._id}`}
                    >
                      {strings.orders.continueOrder}
                    </Button>
                  ) : (
                    <Button
                      color="outlined"
                      size="sm"
                      onClick={() => history.push(getRouteMailOnDemandOrder(order._id))}
                    >
                      {strings.orders.viewOrder}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const MailOnDemandOrdersPage = () => (
  <DashboardTemplate hasSidebar title={strings.orders.pageTitle}>
    <MailOnDemandOrdersList />
  </DashboardTemplate>
);

export default MailOnDemandOrdersPage;
